import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { athenaApi, useLazyGetProjectSummaryWithAttachmentsQuery } from '../../../app/athenaApi';
import { isFormTypeAvailable } from '../../utilities';
import {
  useDeleteApiProjectsByIdMutation,
  useLazyGetApiCtpsQuery,
  useLazyGetApiDeliverablesQuery,
  useLazyGetApiLocationsByIdQuery,
  useLazyGetApiPnrsQuery,
  useLazyGetApiRipsQuery,
  useLazyGetApiTarsQuery,
  useLazyGetApiTripReportsQuery,
  useLazyGetApiUsersQuery,
} from '../../../app/athenaApiGen';
import { openEditModal } from '../../../app/projects/projectsSlice';
import NoPermissionsRows from '../../../components/DataGrid/NoPermissionsRows';
import DeleteConfirmation from '../../../components/Modals/DeleteConfirmation';
import { StyledDataGridPro } from '../../../shared-theme/StyledComponents';
import { TABLE_PAGE_SIZE_OPTIONS } from '../../../utils/tableVariables';
import { useProjectPermissions } from '../useProjectPermissions';
import { getProjectColumns } from './ConstProjectColumns';
import ProjectsEmptyOverlay from './ProjectsEmptyOverlay';
import ProjectDownloaderOptionsDialog from './ProjectDownloaderOptionsDialog';
import ProjectTypesPanel from './ProjectTypesPanel';

const DEBUG = false;

function ProjectTable({
  projects,
  totalCount,
  loading: externalLoading,
  isError: externalIsError,
  page,
  pageSize,
  showNoPermissions,
  onPageChange,
  onPageSizeChange,
  onViewProject,
  selectedRowId,
  deepLinkProjectId,
  onDeepLinkComplete,
  search,
  onClearSearch,
  isSearchTransitioning,
}) {
  const dispatch = useDispatch();

  // Get permission checking utilities from ACL hook
  const { hasProjectAction, isAdmin } = useProjectPermissions();

  // Permission check function that can be called per-row with specific project/program IDs
  // hasProjectAction now handles both project-scoped AND program-scoped roles
  const checkPermission = React.useCallback(
    (actionName, projectId, programId) => {
      // Admin users have all permissions
      if (isAdmin) return true;

      // hasProjectAction checks both project-scoped and program-scoped permissions
      // For program-scoped roles (like Program Viewer), having the action at program level
      // grants access to all projects within that program
      return hasProjectAction(actionName, projectId, programId);
    },
    [hasProjectAction, isAdmin],
  );
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(!DEBUG);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [menuRowId, setMenuRowId] = React.useState(null);
  const [usersMap, setUsersMap] = React.useState({});
  const [locationsMap, setLocationsMap] = React.useState({});
  const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);
  const [projectToArchive, setProjectToArchive] = React.useState(null);
  const [downloadProject, setDownloadProject] = React.useState(null);
  // Generation token — incremented every time `projects` changes so async
  // enrichment from a previous list can't overwrite rows for the current one.
  const enrichmentGenerationRef = React.useRef(0);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const [fetchProjectDeliverables] = useLazyGetApiDeliverablesQuery();
  const [fetchProjectSummary] = useLazyGetProjectSummaryWithAttachmentsQuery();
  const [fetchUsers] = useLazyGetApiUsersQuery();
  const [fetchLocationById] = useLazyGetApiLocationsByIdQuery();
  const [deleteProject, { isLoading: deleting }] =
    useDeleteApiProjectsByIdMutation();
  const [fetchCtps] = useLazyGetApiCtpsQuery();
  const [fetchPnrs] = useLazyGetApiPnrsQuery();
  const [fetchRips] = useLazyGetApiRipsQuery();
  const [fetchTars] = useLazyGetApiTarsQuery();
  const [fetchTrs] = useLazyGetApiTripReportsQuery();
  const availableForms = useSelector((state) => state.user.availableForms);

  // Fetch users once on mount (if user has permission)
  // Location names are fetched per-project using project.locationId
  // Note: This will fail for non-admin users who don't have list-users permission
  // Skip if showNoPermissions is true OR if not admin (users endpoint is admin-only)
  React.useEffect(() => {
    if (showNoPermissions || !isAdmin) return;

    const fetchAllUsers = async () => {
      try {
        const usersData = await fetchUsers().unwrap();
        // Create a map of contactId -> { userName, userId } for quick lookup
        const map = {};
        if (Array.isArray(usersData)) {
          usersData.forEach((user) => {
            if (user.contact?.contactId) {
              map[user.contact.contactId] = {
                userName: user.userName || user.contact.name || 'Unknown User',
                userId: user.id,
              };
            }
          });
        }
        setUsersMap(map);
      } catch (error) {
        // Expected for non-admin users without list-users permission
        // Owner names will show as "Unknown" but table will still work
        if (error?.status === 403) {
          // Silently ignore permission errors - user names will show as fallback
        } else {
          console.error('Error fetching users:', error);
        }
      }
    };

    fetchAllUsers();
  }, [fetchUsers, showNoPermissions, isAdmin]);

  // Archive project handler - uses DELETE endpoint to trigger cascade archive
  // DELETE /api/projects/{id} calls CascadeArchiveProject() which archives:
  // - The project itself
  // - All project attachments
  // - All library directories under the project
  // - All attachments in those directories
  const handleArchiveProject = async () => {
    if (!projectToArchive) return;

    try {
      await deleteProject({
        id: projectToArchive.projectId,
      }).unwrap();

      // Invalidate Library caches to remove orphaned items from tree
      dispatch(
        athenaApi.util.invalidateTags(['Library', 'Library Directories']),
      );

      // Close dialog and reset state
      setArchiveDialogOpen(false);
      setProjectToArchive(null);
      setSnackbar({
        open: true,
        message: 'Project deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error archiving project:', error);
      if (error?.status === 403) {
        setSnackbar({
          open: true,
          message: 'You do not have permission to delete this project',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete project. Please try again.',
          severity: 'error',
        });
      }
      setArchiveDialogOpen(false);
      setProjectToArchive(null);
    }
  };

  const processProjects = React.useCallback(
    async (projectsList) => {
      // Capture the generation this run belongs to. If projects change while
      // we're awaiting enrichment, this snapshot will mismatch the ref and we
      // bail before writing stale rows.
      const myGeneration = enrichmentGenerationRef.current;
      const isStale = () => enrichmentGenerationRef.current !== myGeneration;

      if (!projectsList || projectsList.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Paint cheap rows from the projects prop immediately so the table
        // reflects the new result set the moment the search returns. Without
        // this, switching from a narrow search to a broader one leaves the
        // previous (smaller) enriched row set visible until the new
        // enrichment fan-out completes — multiple seconds for ~40 parallel
        // per-project fetches.
        if (!isStale()) {
          setRows(
            projectsList.map((project) => ({
              id: project.projectId,
              projectId: project.projectId,
              name: project.title,
              description: project.description,
              code: project.projectCode,
              status: project.status || 'In Progress',
              workflowState: project.workflowState || null,
              location: '-',
              deliverables: [],
              completion: 0,
              rgb: 'rgba(255, 183, 0, 0.62)',
              action: 'Edit',
              creationDate: null,
              lastModified: null,
              ownerName: project.ownerName || '-',
              ownerUserId: null,
              project,
              hasApprovedForms: false,
              summary: null,
            })),
          );
        }

        const allProjects = projectsList;

        // Helper to fetch location name by ID
        const fetchLocationName = async (locationId) => {
          if (!locationId) return null;

          // Check cache first
          if (locationsMap[locationId]) {
            return locationsMap[locationId];
          }

          try {
            const locationData = await fetchLocationById({
              id: locationId,
            }).unwrap();
            const locationName =
              locationData.name ||
              locationData.locationCode ||
              'Unknown Location';

            // Update cache
            setLocationsMap((prev) => ({
              ...prev,
              [locationId]: locationName,
            }));

            return locationName;
          } catch (error) {
            console.error(`Error fetching location ${locationId}:`, error);
            return null;
          }
        };

        // Helpers per project
        const fetchProjectSummaryData = async (project) => {
          try {
            const data = await fetchProjectSummary({
              id: project.projectId,
            }).unwrap();

            return { summary: data, is404: false };
          } catch (e) {
            // 404 is expected when summary doesn't exist yet - don't log as error
            if (e?.status !== 404) {
              console.error(
                `Failed to fetch summary for project ${project.projectId}:`,
                e,
              );
            }
            return { summary: null, is404: e?.status === 404 };
          }
        };

        const fetchAllDeliverablesForProject = async (projectId) => {
          try {
            const all = [];
            let delPage = 1;
            const delPageSize = 20;
            const MAX_PAGES = 250;
            let failed = false;

            while (delPage <= MAX_PAGES) {
              try {
                const data = await fetchProjectDeliverables({
                  projectId,
                  page: delPage,
                  limit: delPageSize,
                }).unwrap();

                const items = data.deliverables || [];
                const total = Number.isFinite(data.total)
                  ? data.total
                  : items.length;
                const ps = Number.isFinite(data.limit)
                  ? data.limit
                  : items.length;
                const currentPage = Number.isFinite(data.page)
                  ? data.page
                  : delPage;

                all.push(...items);

                const totalPages = ps > 0 ? Math.ceil(total / ps) : 1;
                if (currentPage >= totalPages || all.length >= total) {
                  break;
                }
                delPage += 1;
              } catch (err) {
                console.warn(
                  `Failed to fetch deliverable page ${delPage} for project ${projectId}:`,
                  err,
                );
                failed = true;
                break;
              }
            }

            return { list: all, is404: false, failed };
          } catch (e) {
            console.warn(
              `Failed to fetch deliverables for project ${projectId}:`,
              e,
            );
            return { list: [], is404: false, failed: true };
          }
        };

        const checkApprovedForms = async (projectId) => {
          try {
            let anyFailed = false;
            const maybeFetch = (formType, fetcher, listKey) => {
              if (!isFormTypeAvailable(availableForms, formType)) {
                return Promise.resolve([]);
              }
              return fetcher({ projectId, page: 1, pageSize: 100 })
                .unwrap()
                .then((res) => res?.[listKey] || [])
                .catch((err) => {
                  console.warn(
                    `${formType.toUpperCase()} fetch failed for project`,
                    projectId,
                    err,
                  );
                  anyFailed = true;
                  return [];
                });
            };

            const [ctps, pnrs, rips, tars, trs] = await Promise.all([
              maybeFetch('ctp', fetchCtps, 'ctps'),
              maybeFetch('pnr', fetchPnrs, 'pnrs'),
              maybeFetch('rip', fetchRips, 'rips'),
              maybeFetch('tar', fetchTars, 'tars'),
              maybeFetch('tr', fetchTrs, 'items'),
            ]);
            const allForms = [...ctps, ...pnrs, ...rips, ...tars, ...trs];
            const hasApproved = allForms.some(
              (f) =>
                f.workflowState &&
                f.workflowState.toLowerCase().includes('approved'),
            );
            // Fail-closed: if any enabled form type errored and we didn't find
            // an approved form in the successful ones, block deletion.
            if (anyFailed && !hasApproved) {
              return true;
            }
            return hasApproved;
          } catch {
            console.warn(
              'checkApprovedForms failed entirely for project',
              projectId,
            );
            return true;
          }
        };

        // For each project, fetch summary and all deliverables
        const projectsMerged = await Promise.all(
          allProjects.map(async (project) => {
            const [
              { summary },
              { list: deliverables, failed: deliverablesFetchFailed },
              hasApprovedForms,
            ] = await Promise.all([
              fetchProjectSummaryData(project),
              fetchAllDeliverablesForProject(project.projectId),
              checkApprovedForms(project.projectId),
            ]);

            // Fetch location name using project.locationId
            const locationName = await fetchLocationName(project.locationId);

            const finalSummary = summary || null;

            let finalDeliverables = Array.isArray(deliverables)
              ? deliverables
              : [];
            // Map to table-ready structure while carrying the raw data too
            return {
              // DataGrid requires an id field
              id: project.projectId,
              projectId: project.projectId,
              name: project.title,
              description: project.description,
              code: project.projectCode,
              status: project.status || finalSummary?.status || 'In Progress',
              workflowState: project.workflowState || null,
              // Use location name fetched from API via project.locationId
              location: locationName || '-',
              deliverables: (finalDeliverables || []).map((d) => ({
                deliverableId: d.deliverableId,
                deliverableName:
                  d.deliverableTitle ||
                  d.deliverableType?.title ||
                  d.deliverableName ||
                  'Untitled Deliverable',
                locationName: d.location?.name || d.locationName || '-',
                description: d.description,
                dateSubmitted: d.dateSubmitted,
                expectedApprovalStart: d.expectedApprovalStart,
                expectedApprovalEnd: d.expectedApprovalEnd,
                ...d,
              })),
              completion: Math.floor(Math.random() * 101),
              rgb: 'rgba(255, 183, 0, 0.62)',
              action: 'Edit',
              creationDate: (finalSummary && finalSummary.dateCreated) || null,
              lastModified: (finalSummary && finalSummary.lastModified) || null,
              ownerName:
                (finalSummary && finalSummary.ownerName) ||
                project.ownerName ||
                (project.ownerId && isAdmin
                  ? usersMap[project.ownerId]?.userName || 'Unknown'
                  : null) ||
                '-',
              ownerUserId: project.ownerId
                ? usersMap[project.ownerId]?.userId || null
                : null,
              project,
              hasApprovedForms,
              deliverablesFetchFailed: Boolean(deliverablesFetchFailed),
              summary: finalSummary,
            };
          }),
        );

        // Bail before writing rows if a newer projects list has arrived. The
        // common trigger is the user typing in the search box while a previous
        // enrichment is still in flight — without this guard the late write
        // overwrites the empty-result state with stale rows.
        if (isStale()) {
          return;
        }

        const finalRows =
          Array.isArray(projectsMerged) && projectsMerged.length > 0
            ? projectsMerged
            : [];
        setRows(finalRows);

        // Handle deep linking if projectId is in URL
        if (deepLinkProjectId && finalRows.length > 0) {
          const project = finalRows.find(
            (row) => row.projectId === deepLinkProjectId,
          );
          if (project && onViewProject) {
            onViewProject(project);
            if (onDeepLinkComplete) {
              onDeepLinkComplete();
            }
          }
        }
      } catch (error) {
        console.error('Error fetching projects and deliverables:', error);
        if (!isStale()) {
          setRows([]);
        }
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    },
    [
      fetchProjectDeliverables,
      fetchProjectSummary,
      fetchCtps,
      fetchPnrs,
      fetchRips,
      fetchTars,
      fetchTrs,
      usersMap,
      isAdmin,
      locationsMap,
      fetchLocationById,
      deepLinkProjectId,
      onViewProject,
      onDeepLinkComplete,
      availableForms,
    ],
  );

  React.useEffect(() => {
    // Invalidate any in-flight enrichment from a previous projects list so
    // its late setRows/setLoading writes are dropped (see processProjects).
    enrichmentGenerationRef.current += 1;
    if (projects && !externalLoading) {
      processProjects(projects);
    } else if (externalLoading) {
      setLoading(true);
    }
  }, [projects, externalLoading, processProjects]);

  const columns = getProjectColumns({
    menuAnchorEl,
    menuRowId,
    setMenuAnchorEl,
    setMenuRowId,
    onViewProject,
    dispatch,
    openEditModal,
    setProjectToArchive,
    setArchiveDialogOpen,
    setDownloadProject,
    checkPermission,
  });

  if (loading && rows.length === 0) {
    // Skeleton table during the initial fetch. Mirrors the actual column
    // layout (Name | Code | Last Modified | Owner | Status | Attachments
    // | actions) so when real data lands, the page doesn't visibly
    // reflow. Reads as "data is coming" instead of "the table is empty."
    const skeletonCols = [
      { flex: 1, minWidth: 200 }, // Project Name
      { width: 120 }, // Code
      { width: 150 }, // Last Modified
      { width: 150 }, // Owner
      { width: 180 }, // Status (chip-sized)
      { width: 130 }, // Attachments
      { width: 80 }, // Actions
    ];
    return (
      <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden', p: 0 }}>
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'action.hover',
          }}
        >
          {skeletonCols.map((col, i) => (
            <Box key={i} sx={{ flex: col.flex, width: col.width, minWidth: col.minWidth }}>
              <Skeleton variant="text" width="60%" sx={{ fontSize: '0.875rem' }} />
            </Box>
          ))}
        </Box>
        {/* Skeleton rows — 8 of them gives a sense of a populated grid
            without dominating the viewport while real data loads. */}
        {Array.from({ length: 8 }).map((_, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {skeletonCols.map((col, i) => (
              <Box key={i} sx={{ flex: col.flex, width: col.width, minWidth: col.minWidth }}>
                <Skeleton
                  variant={i === 4 ? 'rounded' : 'text'}
                  width={i === 4 ? '90%' : `${50 + ((rowIdx * 7 + i * 11) % 35)}%`}
                  height={i === 4 ? 24 : undefined}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <StyledDataGridPro
        rows={showNoPermissions ? [] : rows}
        columns={columns}
        paginationMode="server"
        pagination
        loading={loading}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          onPageChange(model.page);
          onPageSizeChange(model.pageSize);
        }}
        rowCount={totalCount}
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          if (onViewProject) {
            onViewProject(params.row);
          }
        }}
        getRowClassName={(params) =>
          params.id === selectedRowId ? 'selected-project-row' : ''
        }
        getDetailPanelContent={({ row }) => <ProjectTypesPanel row={row} />}
        getDetailPanelHeight={() => 'auto'}
        slots={{
          noRowsOverlay: showNoPermissions ? NoPermissionsRows : ProjectsEmptyOverlay,
          detailPanelExpandIcon: KeyboardArrowDownIcon,
          detailPanelCollapseIcon: KeyboardArrowUpIcon,
        }}
        slotProps={{
          noRowsOverlay: showNoPermissions
            ? undefined
            : { search, onClearSearch, isSearchTransitioning },
          pagination: {
            showFirstButton: true,
            showLastButton: true,
          },
        }}
        hideFooterPagination={showNoPermissions}
        sx={{
          // Suppress DataGrid's default cell + column-header focus outlines.
          // They render as a thin blue line on click, which feels jarring
          // since row selection is already conveyed by the highlighted row
          // and the open View Panel. Keyboard a11y is preserved via
          // :focus-visible on the row itself (Mui-selected styling).
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          // Suppress the LinearProgress bar DataGrid shows between header
          // and rows while loading=true. It flashes on click because the
          // refetch keys change briefly when the deep-link effect fires;
          // the bar reads as a "scrolling blue line" against the otherwise
          // static layout. Keyboard / loading state is still conveyed by
          // the row count and the table skeleton on first paint.
          '& .MuiLinearProgress-root': {
            display: 'none',
          },
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"]': {
            textOverflow: 'clip',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"] .MuiIconButton-root': {
            padding: '4px !important',
            minWidth: 'auto !important',
            backgroundColor: 'transparent !important',
          },
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"] .MuiIconButton-root::before': {
            content: '""',
            display: 'none !important',
          },
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"] .MuiIconButton-root::after': {
            content: '""',
            display: 'none !important',
          },
        }}
      />

      {/* Archive Confirmation Dialog */}
      <DeleteConfirmation
        open={archiveDialogOpen}
        title="Delete this project?"
        warning
        onClose={() => {
          setArchiveDialogOpen(false);
          setProjectToArchive(null);
        }}
        content={{
          mainText: `Are you sure you want to delete the project <strong>${projectToArchive?.projectName}</strong> (${projectToArchive?.projectCode})?`,
          warningText: `<strong>Warning:</strong> This action will also archive all related data including:`,
          customContent: (
            <Box
              component="ul"
              sx={{ color: 'error.main', mt: 1, mb: 0, pl: 3 }}
            >
              <li>Project Summaries (all versions)</li>
              <li>Attachments</li>
              <li>
                All Deliverables and their child records (TAR, CTP, RIP, TR,
                PNR)
              </li>
              <li>Comments, Tags, and Workflows</li>
            </Box>
          ),
        }}
        handleConfirmation={handleArchiveProject}
        handleCancellation={() => {
          setArchiveDialogOpen(false);
          setProjectToArchive(null);
        }}
        isLoading={deleting}
      />

      {/* Project Download Component */}
      <ProjectDownloaderOptionsDialog
        open={Boolean(downloadProject)}
        onClose={() => setDownloadProject(null)}
        project={downloadProject}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

ProjectTable.propTypes = {
  projects: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  isError: PropTypes.bool,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  showNoPermissions: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onViewProject: PropTypes.func,
};

export default ProjectTable;
